/**
 * The session's campaign artifacts: an in-memory list, persisted behind it.
 *
 * In-memory is not an optimization here, it is the requirement. `SavedState.Capture`
 * is synchronous and reads the workspace and the redux singletons; it cannot await
 * IndexedDB and it cannot reach into a React component's state, which is where this
 * list used to live. So the list has to be readable synchronously from outside the
 * component tree — hence a registry with `getSnapshot`/`subscribe`, the same shape
 * as `compoundAliasRegistry`, with the store written through behind it.
 *
 * Records hold alias bindings, and those hold real display names. Browser-side
 * only: never attach a snapshot from here to a wire snapshot, a submission or a
 * decision.
 *
 * @module deputy/campaignArtifactRegistry
 */

import {
    campaignArtifactRecord,
    deserializeCampaignArtifacts,
    mergeCampaignArtifacts,
} from '../gibbs/autonomousCampaign/narrative/campaignArtifacts';
import { formatMaskedMarkdown, formatMaskedText } from './compoundAliasRegistry';
import { defaultCampaignArtifactStore } from './campaignArtifactStore';

const EMPTY = Object.freeze({ artifacts: Object.freeze([]) });

export function createCampaignArtifactRegistry({
    store = defaultCampaignArtifactStore,
    now = () => Date.now(),
} = {}) {
    let current = EMPTY;
    const listeners = new Set();

    function notify() {
        listeners.forEach((listener) => {
            try {
                listener();
            } catch (_) {
                // A listener must not be able to break campaign execution.
            }
        });
    }

    /**
     * Stable while nothing changes, which is what `useSyncExternalStore` needs:
     * a fresh object every call would re-render forever.
     */
    function getSnapshot() {
        return current;
    }

    function subscribe(listener) {
        if (typeof listener !== 'function') return () => {};
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    function commit(artifacts) {
        if (artifacts === current.artifacts) return current;
        current = { artifacts };
        notify();
        return current;
    }

    /**
     * Keep one document this session produced.
     *
     * @param {object} input see `campaignArtifactRecord`; `createdAt` is stamped here
     * @returns {object|null} the stored record, or null when there was nothing to store
     */
    function record(input) {
        const artifact = campaignArtifactRecord({ createdAt: now(), ...input });
        if (!artifact) return null;
        const merged = mergeCampaignArtifacts(current.artifacts, [artifact]);
        if (merged === current.artifacts) return null;
        commit(merged);
        Promise.resolve(store?.putArtifact?.(artifact)).catch(() => {});
        return artifact;
    }

    /**
     * Take in artifacts read out of a session file. They are marked restored, so
     * nothing here can present itself as a run that just happened.
     *
     * @param {unknown} raw the file's `CampaignArtifacts` field, from any version
     */
    function restoreFromFile(raw) {
        const restored = deserializeCampaignArtifacts(raw);
        if (restored.length === 0) return current;
        const merged = mergeCampaignArtifacts(current.artifacts, restored);
        if (merged === current.artifacts) return current;
        commit(merged);
        restored.forEach((artifact) => {
            Promise.resolve(store?.putArtifact?.(artifact)).catch(() => {});
        });
        return current;
    }

    /** Load what earlier sessions left behind. Survives a reload and a new tab. */
    async function hydrate() {
        try {
            const stored = await store?.listArtifacts?.();
            const merged = mergeCampaignArtifacts(current.artifacts, stored || []);
            if (merged !== current.artifacts) commit(merged);
        } catch (_) {
            // Best effort; a failed read must not cost the live list.
        }
        return current;
    }

    /** The user discarding the list. Deliberate and explicit, never a side effect. */
    function clear() {
        if (current.artifacts.length === 0) return;
        commit(EMPTY.artifacts);
        Promise.resolve(store?.clearArtifacts?.()).catch(() => {});
    }

    /**
     * The readable copy of one document, decoded with the bindings that document
     * was written with — not with the live registry, which is keyed by a workspace
     * fingerprint that a reordering restore invalidates.
     */
    function formatArtifactMarkdown(artifact, options = { includeMaskedId: false }) {
        return formatMaskedMarkdown(artifact?.narrative, artifact?.aliasBindings, options);
    }

    function formatArtifactText(artifact, options = { includeMaskedId: false }) {
        return formatMaskedText(artifact?.narrative, artifact?.aliasBindings, options);
    }

    /**
     * A text formatter bound to one document's bindings, which is the contract
     * `CompletionBlock` renders through. One per artifact rather than one shared
     * one: a session can hold documents written against different workspaces, and
     * a single live formatter would decode all of them with the latest one's map.
     */
    function markdownFormatterFor(artifact, options = { includeMaskedId: false }) {
        return (text) => formatMaskedMarkdown(text, artifact?.aliasBindings, options);
    }

    function resetForTests() {
        current = EMPTY;
        notify();
    }

    return {
        getSnapshot,
        subscribe,
        record,
        restoreFromFile,
        hydrate,
        clear,
        formatArtifactMarkdown,
        formatArtifactText,
        markdownFormatterFor,
        resetForTests,
    };
}

export const defaultCampaignArtifactRegistry = createCampaignArtifactRegistry();
